import { collection, doc, addDoc, updateDoc, getDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Subscription, PaymentHistory, BillingInfo } from '../types/subscription';

export class SubscriptionService {
  static async createSubscription(data: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'>) {
    const subscriptionRef = await addDoc(collection(db, 'subscriptions'), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return subscriptionRef.id;
  }

  static async updateSubscription(subscriptionId: string, data: Partial<Subscription>) {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  static async getSubscriptionBySchool(schoolId: string): Promise<Subscription | null> {
    const q = query(
      collection(db, 'subscriptions'),
      where('schoolId', '==', schoolId)
    );
    
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      id: doc.id,
      ...data,
      currentPeriodStart: data.currentPeriodStart.toDate(),
      currentPeriodEnd: data.currentPeriodEnd.toDate(),
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    } as Subscription;
  }

  static async addPaymentHistory(payment: Omit<PaymentHistory, 'id' | 'createdAt'>) {
    await addDoc(collection(db, 'paymentHistory'), {
      ...payment,
      createdAt: serverTimestamp(),
    });
  }

  static async getPaymentHistory(schoolId: string): Promise<PaymentHistory[]> {
    const q = query(
      collection(db, 'paymentHistory'),
      where('schoolId', '==', schoolId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
    })) as PaymentHistory[];
  }

  static async saveBillingInfo(schoolId: string, billingInfo: BillingInfo) {
    const billingRef = doc(db, 'billing', schoolId);
    await updateDoc(billingRef, {
      ...billingInfo,
      updatedAt: serverTimestamp(),
    });
  }

  static async getBillingInfo(schoolId: string): Promise<BillingInfo | null> {
    const billingRef = doc(db, 'billing', schoolId);
    const billingDoc = await getDoc(billingRef);
    
    if (!billingDoc.exists()) return null;
    
    return billingDoc.data() as BillingInfo;
  }

  static async updateSubscriptionStatus(
    stripeSubscriptionId: string,
    status: Subscription['status']
  ) {
    const q = query(
      collection(db, 'subscriptions'),
      where('stripeSubscriptionId', '==', stripeSubscriptionId)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const subscriptionDoc = querySnapshot.docs[0];
      await updateDoc(subscriptionDoc.ref, {
        status,
        updatedAt: serverTimestamp(),
      });
    }
  }

  static async handleWebhook(eventType: string, data: any) {
    switch (eventType) {
      case 'invoice.payment_succeeded':
        await this.handlePaymentSucceeded(data);
        break;
      case 'invoice.payment_failed':
        await this.handlePaymentFailed(data);
        break;
      case 'customer.subscription.updated':
        await this.handleSubscriptionUpdated(data);
        break;
      case 'customer.subscription.deleted':
        await this.handleSubscriptionDeleted(data);
        break;
      default:
        console.log(`Unhandled webhook event: ${eventType}`);
    }
  }

  private static async handlePaymentSucceeded(invoice: any) {
    const subscription = invoice.subscription;
    const customerId = invoice.customer;
    
    // Update subscription status
    await this.updateSubscriptionStatus(subscription, 'active');
    
    // Add payment history
    const q = query(
      collection(db, 'subscriptions'),
      where('stripeCustomerId', '==', customerId)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const subscriptionDoc = querySnapshot.docs[0];
      const subscriptionData = subscriptionDoc.data();
      
      await this.addPaymentHistory({
        schoolId: subscriptionData.schoolId,
        amount: invoice.amount_paid,
        currency: invoice.currency,
        status: 'succeeded',
        description: `Payment for subscription ${subscription}`,
        stripePaymentIntentId: invoice.payment_intent,
      });
    }
  }

  private static async handlePaymentFailed(invoice: any) {
    const subscription = invoice.subscription;
    const customerId = invoice.customer;
    
    // Update subscription status
    await this.updateSubscriptionStatus(subscription, 'past_due');
    
    // Add payment history
    const q = query(
      collection(db, 'subscriptions'),
      where('stripeCustomerId', '==', customerId)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const subscriptionDoc = querySnapshot.docs[0];
      const subscriptionData = subscriptionDoc.data();
      
      await this.addPaymentHistory({
        schoolId: subscriptionData.schoolId,
        amount: invoice.amount_due,
        currency: invoice.currency,
        status: 'failed',
        description: `Failed payment for subscription ${subscription}`,
        stripePaymentIntentId: invoice.payment_intent,
      });
    }
  }

  private static async handleSubscriptionUpdated(subscription: any) {
    const q = query(
      collection(db, 'subscriptions'),
      where('stripeSubscriptionId', '==', subscription.id)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const subscriptionDoc = querySnapshot.docs[0];
      
      await updateDoc(subscriptionDoc.ref, {
        status: subscription.status,
        currentPeriodStart: new Date(subscription.current_period_start * 1000),
        currentPeriodEnd: new Date(subscription.current_period_end * 1000),
        cancelAtPeriodEnd: subscription.cancel_at_period_end,
        updatedAt: serverTimestamp(),
      });
    }
  }

  private static async handleSubscriptionDeleted(subscription: any) {
    await this.updateSubscriptionStatus(subscription.id, 'canceled');
  }
}